<script setup lang="ts">
/**
 * 校验页打回意见横幅：展示最新一轮意见，可展开历史。
 */
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'

import {
  getTaskReviewRecords,
  type TaskReviewRecord,
} from '@/modules/task/api/task'

const props = defineProps<{
  taskId?: number | null
  /** 当前轮次（>0 且有记录时显示横幅） */
  round?: number
}>()

const { t } = useI18n()
const loading = ref(false)
const records = ref<TaskReviewRecord[]>([])
const historyOpen = ref(false)

const sorted = computed(() =>
  [...records.value].sort((a, b) => a.round - b.round),
)
const latest = computed(() => {
  if (!sorted.value.length) return null
  return sorted.value[sorted.value.length - 1]
})
const history = computed(() =>
  sorted.value.length > 1 ? sorted.value.slice(0, -1) : [],
)
const visible = computed(() => !!props.taskId && !!latest.value)

async function load() {
  if (!props.taskId) {
    records.value = []
    return
  }
  loading.value = true
  try {
    records.value = await getTaskReviewRecords(props.taskId)
  } catch (e) {
    console.error(e)
    records.value = []
  } finally {
    loading.value = false
  }
}

onMounted(load)
watch(() => props.taskId, load)

function formatTime(value?: string | null) {
  if (!value) return ''
  return dayjs(value).format('YYYY-MM-DD HH:mm')
}
</script>

<template>
  <a-alert
    v-if="visible"
    type="warning"
    show-icon
    class="revise-comment-banner"
    :message="t('task.latestReviseComment', { n: latest?.round ?? round })"
  >
    <template #description>
      <a-spin :spinning="loading">
        <p class="latest-comment">{{ latest?.comment }}</p>
        <div v-if="latest?.created_at" class="meta">
          {{ formatTime(latest.created_at) }}
        </div>
        <a-button
          v-if="history.length"
          type="link"
          size="small"
          class="history-toggle"
          @click="historyOpen = !historyOpen"
        >
          {{ historyOpen ? t('task.hideReviseHistory') : t('task.showReviseHistory') }}
        </a-button>
        <ul v-if="historyOpen && history.length" class="history-list">
          <li v-for="item in history" :key="item.id">
            <strong>{{ t('task.roundBadge', { n: item.round }) }}</strong>
            <span>{{ item.comment }}</span>
            <span class="meta">{{ formatTime(item.created_at) }}</span>
          </li>
        </ul>
      </a-spin>
    </template>
  </a-alert>
</template>

<style scoped>
.revise-comment-banner {
  margin-bottom: 16px;
}
.latest-comment {
  margin: 0 0 4px;
  white-space: pre-wrap;
}
.meta {
  color: #8c8c8c;
  font-size: 12px;
}
.history-toggle {
  padding-left: 0;
}
.history-list {
  margin: 8px 0 0;
  padding-left: 18px;
}
.history-list li {
  margin-bottom: 6px;
}
.history-list strong {
  margin-right: 8px;
}
</style>
